/**
 * Copyright (C) 2021 Tencent.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createElement } from '@/utils/dom';

const SAFE_AREA_MARGIN = 15;

/**
 * Cherry实现了将粘贴的html内容转成对应的markdown源码的功能
 * 本工具主要实现将粘贴html转成的markdown源码在编辑器中选中，并给出切换按钮
 * 可以切换为纯文本内容，或者markdown内容
 */
const pasteHelper = {
  /**
   * 核心方法，粘贴后展示切换按钮
   * 只有粘贴html时才会出现切换按钮
   * @param {Object} cherry - Cherry 编辑器实例
   * @param {number} currentCursor - 当前光标位置（文档偏移量）
   * @param {Object} editorView - 编辑器视图（CM6 适配器）
   * @param {string} html - HTML 的纯文本内容
   * @param {string} md - HTML 对应的 Markdown 源码
   * @returns {void}
   */
  showSwitchBtnAfterPasteHtml(cherry, currentCursor, editorView, html, md) {
    if (html.trim() === md.trim()) {
      return;
    }
    this.init(cherry, currentCursor, editorView, html, md);
    this.bindListener();
    this.initBubble();
    this.showBubble();
    // 默认粘贴成markdown格式，如果用户上次选择粘贴为纯文本，则需要切换为text
    if (this.getTypeFromLocalStorage() === 'text') {
      this.switchTextClick();
    }
  },

  /**
   * 初始化粘贴助手的内部状态
   * @param {Object} cherry - Cherry 编辑器实例
   * @param {number} currentCursor - 当前光标位置（文档偏移量）
   * @param {Object} editorView - 编辑器视图
   * @param {string} html - HTML 的纯文本内容
   * @param {string} md - HTML 对应的 Markdown 源码
   */
  init(cherry, currentCursor, editorView, html, md) {
    this.cherry = cherry;
    this.html = html;
    this.md = md;
    this.currentCursor = currentCursor;
    // 记录粘贴区域，切换 TEXT/Markdown 时按范围替换，避免选区丢失导致内容重复
    this.pasteFrom = currentCursor;
    this.pasteTo = currentCursor + md.length;
    this.codemirror = editorView;
    this.locale = cherry.locale;
  },

  /**
   * 获取缓存中的复制粘贴类型
   */
  getTypeFromLocalStorage() {
    if (typeof localStorage === 'undefined') {
      return 'md';
    }
    return localStorage.getItem('cherry-paste-type') || 'md';
  },

  /**
   * 记忆最近一次用户选择的粘贴类型
   */
  setTypeToLocalStorage(type) {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem('cherry-paste-type', type);
  },

  /**
   * 在编辑器中自动选中刚刚粘贴的内容
   * CM6: currentCursor 和 getCursor() 都是文档偏移量
   */
  setSelection() {
    // CM6: setSelection(anchor, head) 使用文档偏移量
    this.codemirror.setSelection(this.pasteFrom, this.pasteTo);
  },

  /**
   * 按粘贴区域替换内容，并同步更新区域终点
   * @param {string} text - 替换文本
   */
  replacePasteContent(text) {
    this.codemirror.replaceRange(text, this.pasteFrom, this.pasteTo);
    this.pasteTo = this.pasteFrom + text.length;
  },
  /**
   * 绑定事件
   * 当编辑器选中区域改变、内容改变时、滚动时处理气泡位置
   * CodeMirror 6: 使用适配器的事件监听
   * @returns null
   */
  bindListener() {
    if (!this.hasBindListener) {
      this.hasBindListener = true;

      // 使用 CM6 适配器的 on 方法监听事件
      this.codemirror.on('change', () => {
        this.hideBubble();
      });

      this.codemirror.on('cursorActivity', () => {
        this.hideBubble();
      });

      this.codemirror.on('scroll', () => {
        this.updatePositionWhenScroll();
      });
    } else {
      return true;
    }
  },

  isHidden() {
    return this.bubbleDom.style.display === 'none';
  },

  toggleBubbleDisplay() {
    if (this.isHidden()) {
      this.bubbleDom.style.display = '';
      return;
    }
    this.bubbleDom.style.display = 'none';
    return;
  },

  hideBubble() {
    if (this.noHide) {
      return true;
    }
    if (this.isHidden()) {
      return;
    }
    this.toggleBubbleDisplay();
  },

  updatePositionWhenScroll() {
    if (this.isHidden()) {
      return;
    }
    // FIXME: update position when stick to the bottom
    // const isStickToBottom = !this.bubbleDom.style.top;
    const offset = this.bubbleDom.dataset.scrollTop - this.getScrollTop();
    this.bubbleDom.style.marginTop = `${offset}px`;
  },

  getScrollTop() {
    // CodeMirror 6: 从 scrollDOM 获取滚动位置
    return this.codemirror.scrollDOM?.scrollTop || 0;
  },

  showBubble() {
    const { top } = this.getLastSelectedPosition();
    if (this.isHidden()) {
      this.toggleBubbleDisplay();
      this.bubbleDom.style.marginTop = '0';
      this.bubbleDom.dataset.scrollTop = this.getScrollTop();
    }
    /**
     * @type {HTMLDivElement}
     * CodeMirror 6: 使用 view.dom 获取编辑器 DOM 元素
     */
    const codemirrorWrapper = this.codemirror.view?.dom || this.codemirror.scrollDOM?.parentElement;
    if (!codemirrorWrapper) return;

    const maxTop = codemirrorWrapper.clientHeight - this.bubbleDom.getBoundingClientRect().height - SAFE_AREA_MARGIN;

    if (top > maxTop) {
      this.bubbleDom.style.top = '';
      this.bubbleDom.style.bottom = `${SAFE_AREA_MARGIN}px`;
    } else {
      this.bubbleDom.style.top = `${top}px`;
      this.bubbleDom.style.bottom = '';
    }
  },

  initBubble() {
    if (this.bubbleDom) {
      this.bubbleDom.setAttribute('data-type', 'md');
      this.switchMd?.classList.add('active');
      this.switchText?.classList.remove('active');
      return true;
    }
    const dom = createElement('div', 'cherry-bubble cherry-bubble--centered cherry-switch-paste');
    dom.style.display = 'none';

    const switchText = createElement('span', 'cherry-toolbar-button cherry-text-btn', {
      title: this.locale.pastePlain,
    });
    switchText.innerText = 'TEXT';

    const switchMd = createElement('span', 'cherry-toolbar-button cherry-md-btn', {
      title: this.locale.pasteMarkdown,
    });
    switchMd.innerText = 'Markdown';

    this.bubbleDom = dom;
    this.switchText = switchText;
    this.switchMd = switchMd;
    this.bubbleDom.appendChild(switchText);
    this.bubbleDom.appendChild(switchMd);
    this.bubbleDom.setAttribute('data-type', 'md');
    // CodeMirror 6: 使用 view.dom 或 scrollDOM 获取编辑器容器
    const editorContainer = this.codemirror.view?.dom || this.codemirror.scrollDOM?.parentElement;
    if (editorContainer) {
      editorContainer.appendChild(this.bubbleDom);
    }
    this.switchMd.addEventListener('click', this.switchMDClick.bind(this));
    this.switchText.addEventListener('click', this.switchTextClick.bind(this));

    // 首次粘贴内容始终为 Markdown，切换逻辑由 showSwitchBtnAfterPasteHtml 统一处理
    this.switchMd.classList.add('active');
    this.switchText.classList.remove('active');
    this.bubbleDom.setAttribute('data-type', 'md');
  },

  switchMDClick(event) {
    this.setTypeToLocalStorage('md');
    if (this.bubbleDom.getAttribute('data-type') === 'md') {
      return;
    }
    this.noHide = true;
    this.bubbleDom.setAttribute('data-type', 'md');
    this.replacePasteContent(this.md);
    this.setSelection();
    this.showBubble();
    this.switchMd.classList.add('active');
    this.switchText.classList.remove('active');
    this.noHide = false;
  },
  switchTextClick(event) {
    this.setTypeToLocalStorage('text');
    // data-type 在 initBubble 中固定为 md（与刚插入的 Markdown 内容一致），
    // localStorage 触发的自动切换不会被此处拦截；请勿在 initBubble 中按 localStorage 预置 text。
    if (this.bubbleDom.getAttribute('data-type') === 'text') {
      return;
    }
    this.noHide = true;
    this.bubbleDom.setAttribute('data-type', 'text');
    this.replacePasteContent(this.html);
    this.setSelection();
    this.showBubble();
    this.switchText.classList.add('active');
    this.switchMd.classList.remove('active');
    this.noHide = false;
  },

  getLastSelectedPosition() {
    // CodeMirror 6: 选中的元素类名为 cm-selectionBackground
    const editorContainer = this.codemirror.view?.dom || this.codemirror.scrollDOM?.parentElement;
    if (!editorContainer) {
      this.hideBubble();
      return {};
    }

    const selectedObjs = Array.from(editorContainer.getElementsByClassName('cm-selectionBackground') || []);

    let width = 0;
    let top = 0;
    if (selectedObjs.length <= 0) {
      this.hideBubble();
      return {};
    }
    // FIXME: remove redundant width calculation
    for (let key = 0; key < selectedObjs.length; key++) {
      const item = selectedObjs[key];
      const position = item.getBoundingClientRect();
      const tmpWidth = position.left + position.width / 2;
      const tmpTop = position.top + position.height;
      if (tmpTop > top && tmpWidth >= width) {
        top = tmpTop;
      }
      if (tmpWidth > width) {
        width = tmpWidth;
      }
    }
    return { top };
  },
};

export default pasteHelper;
